import React, { forwardRef, useContext, useEffect, useImperativeHandle, useState } from 'react';
import { SelectButton } from 'primereact/selectbutton';
import { ApiContext } from '@api/api';
import { DefaultVersionEnum } from '@api/ringil-api.v2';
import RingilInputSwitch from '@components/framework/input/RingilInputSwitch/RingilInputSwitch.tsx';
import { BETA_SURFACES, BetaFeaturesMap } from '@hooks/useBetaVersion/betaSurfaces';

interface ComponentProps {
  companyId: string;
}

export interface BetaFeaturesSectionHandle {
  // Persists only surfaces that differ from the catalog default, and only when changed. onComplete always fires.
  saveIfChanged: (onComplete?: () => void) => void;
}

interface Row {
  key: string;
  defaultVersion: DefaultVersionEnum;
  switchVisible: boolean;
}

const isDefault = (row: Row): boolean => {
  const def = BETA_SURFACES.find(s => s.key === row.key);
  return !!def && row.defaultVersion === def.defaultVersion && row.switchVisible === def.switchVisible;
};

const toRows = (stored: BetaFeaturesMap): Row[] =>
  BETA_SURFACES.map(def => {
    const cfg = stored[def.key] ?? { defaultVersion: def.defaultVersion, switchVisible: def.switchVisible };
    return { key: def.key, defaultVersion: cfg.defaultVersion, switchVisible: cfg.switchVisible };
  });

// Only overrides (non-default rows) are persisted; default rows stay unconfigured.
const toMap = (rows: Row[]): BetaFeaturesMap =>
  rows.reduce<BetaFeaturesMap>((acc, row) => {
    if (!isDefault(row)) acc[row.key] = { defaultVersion: row.defaultVersion, switchVisible: row.switchVisible };
    return acc;
  }, {});

const fingerprint = (map: BetaFeaturesMap): string =>
  Object.keys(map)
    .sort()
    .map(key => `${key}:${map[key].defaultVersion}:${map[key].switchVisible}`)
    .join('|');

const versionOptions = [
  { label: 'Original', value: DefaultVersionEnum.ORIGINAL },
  { label: 'New', value: DefaultVersionEnum.NEW },
];

const BetaFeaturesSection = forwardRef<BetaFeaturesSectionHandle, ComponentProps>(({ companyId }, ref) => {
  const { getBetaFeatures, setBetaFeatures } = useContext(ApiContext);
  const [rows, setRows] = useState<Row[]>(() => toRows({}));
  const [baseline, setBaseline] = useState<string>('');

  useEffect(() => {
    getBetaFeatures(companyId, d => {
      const stored = (d ?? {}) as BetaFeaturesMap;
      setRows(toRows(stored));
      setBaseline(fingerprint(stored));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useImperativeHandle(ref, () => ({
    saveIfChanged: onComplete => {
      const map = toMap(rows);
      if (fingerprint(map) === baseline) {
        onComplete?.();
        return;
      }
      setBetaFeatures(companyId, { features: map }, () => {
        getBetaFeatures(companyId, d => {
          const stored = (d ?? {}) as BetaFeaturesMap;
          setRows(toRows(stored));
          setBaseline(fingerprint(stored));
        });
        onComplete?.();
      });
    },
  }));

  const patchRow = (key: string, patch: Partial<Row>) => setRows(prev => prev.map(r => (r.key === key ? { ...r, ...patch } : r)));

  return (
    <div className={'flex flex-col gap-4'}>
      {rows.map(row => {
        const def = BETA_SURFACES.find(s => s.key === row.key);
        return (
          <div key={row.key} className={'flex flex-row items-center gap-6'} data-testid={`beta-surface-${row.key}`}>
            <div className={'flex flex-row items-center gap-2 w-[280px]'}>
              <span className={'font-medium'}>{def?.labelFallback ?? row.key}</span>
            </div>
            <SelectButton
              value={row.defaultVersion}
              options={versionOptions}
              onChange={e => e.value && patchRow(row.key, { defaultVersion: e.value as DefaultVersionEnum })}
            />
            {def?.userSwitchable !== false && (
              <RingilInputSwitch value={row.switchVisible} onChange={v => patchRow(row.key, { switchVisible: v })} label={'Show switch'} />
            )}
          </div>
        );
      })}
    </div>
  );
});

BetaFeaturesSection.displayName = 'BetaFeaturesSection';

export default BetaFeaturesSection;
